import React from 'react';
import { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { TextRow, RectShape } from 'react-placeholder/lib/placeholders';
import { useSelector } from 'react-redux';
import ResponseBox from 'commons/src/components/responseMessages/ResponseBox';
import { colors, dateFormats } from 'commons/src/constants';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { Store } from '../../../../../reducers';
import { BusinessRequestType as RequestType } from '../../../../../reducers/BusinessRequestType';
import styles from '../../iaqInsight2.module.scss';
import { SensorReportBase } from '../sensorSection';
import AverageMeasurementsDuringOpeningHours from './AverageMeasurementsDuringOpeningHours';
import BuildingDetails from './BuildingDetails';
import DownloadReportPdfButton from './DownloadReportPdfButton';

export type ReportViewProps = {
    from: Moment;
    to: Moment;
    comment?: string;
};

function ReportView({ from, to, comment = '' }: ReportViewProps): React.ReactElement {
    const { t: txt } = useTranslation();
    const { locationId, sensorReports, thresholds } = useSelector((state: Store) => state.iaqInsight);
    const buildings = useSelector((state: Store) => state.buildings.buildings);
    const locationsLoading = useSelector((state: Store) => state.locations.loading);
    const { dateFormat } = useSelector((state: Store) => state.userSettings);
    const loading = useSelector((state: Store) => state.requests[RequestType.FetchIaqInsightData].loading);

    const building = locationId ? buildings[locationId] : undefined;
    const usageHours = building ? building.usageHours : {};
    const timeZone = (building && building.timezone) || 'UTC';

    const safeSensorReports = sensorReports || {};
    const safeThresholds = thresholds || {};

    const hasReportData = !!(building && from && to && Object.keys(safeSensorReports).length > 0);

    const hideCharts = from && to ? to.diff(from, 'days') > 31 : false;

    const iaqInsight2Placeholder = (
        <div className={styles.skeletonContainer}>
            <div style={{ marginBottom: '24px' }}>
                <TextRow color={colors.greyAsh} style={{ width: '200px', height: '28px', marginBottom: '8px' }} />
                <TextRow color={colors.greyAsh} style={{ width: '300px', height: '16px' }} />
            </div>

            {/* Building info skeleton */}
            <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <TextRow color={colors.greyAsh} style={{ width: '150px', height: '20px' }} />
                <TextRow color={colors.greyAsh} style={{ width: '150px', height: '20px' }} />
                <TextRow color={colors.greyAsh} style={{ width: '150px', height: '20px' }} />
                <TextRow color={colors.greyAsh} style={{ width: '150px', height: '20px' }} />
            </div>

            <div className={styles.divider} />

            {/* Opening hours skeleton */}
            <div style={{ marginTop: '24px', marginBottom: '24px' }}>
                <TextRow color={colors.greyAsh} style={{ width: '120px', height: '20px', marginBottom: '16px' }} />
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    {[1, 2, 3, 4, 5, 6, 7].map(i => (
                        <TextRow key={i} color={colors.greyAsh} style={{ width: '80px', height: '16px' }} />
                    ))}
                </div>
            </div>

            <div className={styles.divider} />

            {/* Sensor metrics skeleton */}
            <div style={{ marginTop: '24px', marginBottom: '24px' }}>
                <TextRow color={colors.greyAsh} style={{ width: '250px', height: '24px', marginBottom: '20px' }} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '170px' }}>
                            <RectShape
                                color={colors.greyAsh}
                                style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                            />
                            <div>
                                <TextRow
                                    color={colors.greyAsh}
                                    style={{ width: '80px', height: '16px', marginBottom: '4px' }}
                                />
                                <TextRow color={colors.greyAsh} style={{ width: '60px', height: '20px' }} />
                            </div>
                        </div>
                    ))}
                </div>
                <TextRow color={colors.greyAsh} style={{ width: '100%', height: '16px', marginTop: '16px' }} />
            </div>

            <div className={styles.divider} />

            {/* Sensor view skeleton */}
            <div style={{ marginTop: '24px' }}>
                <div style={{ marginBottom: '24px' }}>
                    <TextRow color={colors.greyAsh} style={{ width: '150px', height: '28px', marginBottom: '16px' }} />
                    <TextRow color={colors.greyAsh} style={{ width: '100%', height: '16px', marginBottom: '8px' }} />
                    <TextRow color={colors.greyAsh} style={{ width: '90%', height: '16px' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                    {[1, 2, 3, 4].map(i => (
                        <RectShape key={i} color={colors.greyAsh} style={{ height: '100px', borderRadius: '8px' }} />
                    ))}
                </div>
                <RectShape color={colors.greyAsh} style={{ width: '100%', height: '200px', borderRadius: '8px' }} />
            </div>
        </div>
    );

    return (
        <div className={styles.resultContainer}>
            <ReactPlaceholder ready={!loading && !locationsLoading} customPlaceholder={iaqInsight2Placeholder}>
                {building && (
                    <div className={styles.reportContainer}>
                        {/* Inline ReportHeader */}
                        <div className={styles.header}>
                            <div className={styles.headerContent}>
                                <div className={styles.titleRow}>
                                    <h1 className={styles.reportTitle}>{txt('IAQReport2.IAQReport')}</h1>
                                    {hasReportData && from && to && (
                                        <div className={styles.buttonContainer}>
                                            <DownloadReportPdfButton
                                                building={building}
                                                from={from}
                                                to={to}
                                                sensorReports={safeSensorReports}
                                                thresholds={safeThresholds}
                                                dateFormat={dateFormat}
                                                comment={comment}
                                            />
                                        </div>
                                    )}
                                </div>
                                {from && to && (
                                    <p className={styles.introParagraph}>
                                        {txt('IAQReport2.ReportGeneratedPeriod', {
                                            from: from.format(dateFormats[dateFormat].shortFormat),
                                            to: to.format(dateFormats[dateFormat].shortFormat),
                                        })}
                                    </p>
                                )}
                                <h2 className={styles.title}>{building.name}</h2>
                                <p className={styles.address}>{building.address}</p>
                            </div>
                        </div>
                        <BuildingDetails building={building} />
                        <div id="averageMeasurementsSection">
                            <AverageMeasurementsDuringOpeningHours
                                sensorReports={safeSensorReports}
                                thresholds={safeThresholds}
                            />
                        </div>
                        <div className={styles.divider} />
                        <h2 className={styles.airQualityDetailsTitle}>{txt('IAQReport2.AirQualityDetails')}</h2>
                        {/* eslint-disable-next-line @typescript-eslint/explicit-function-return-type */}
                        {(() => {
                            // Define custom sensor order for IAQInsight2: CO2, Temp, Humidity, VOC, PM2.5, PM1, Light, Noise
                            const sensorOrder = [
                                SensorTypes.co2,
                                SensorTypes.temp,
                                SensorTypes.humidity,
                                SensorTypes.voc,
                                SensorTypes.pm25,
                                SensorTypes.pm1,
                                SensorTypes.light,
                                SensorTypes.noise,
                            ];
                            const sensorOrderMap = new Map(sensorOrder.map((sensor, index) => [sensor, index]));
                            const reportableSensorTypes = new Set<SensorTypes>(sensorOrder);

                            const availableSensors = Object.keys(safeSensorReports || {})
                                .filter((sensor): sensor is SensorTypes => {
                                    const sensorData = safeSensorReports[sensor];
                                    const sensorThreshold = safeThresholds[sensor];
                                    return (
                                        !!sensorData &&
                                        !!sensorThreshold &&
                                        reportableSensorTypes.has(sensor as SensorTypes)
                                    );
                                })
                                .sort((sensorA, sensorB) => {
                                    const indexA = sensorOrderMap.get(sensorA as SensorTypes) ?? 999;
                                    const indexB = sensorOrderMap.get(sensorB as SensorTypes) ?? 999;
                                    return indexA - indexB;
                                });

                            if (availableSensors.length === 0) {
                                return <ResponseBox text="NotEnoughData" />;
                            }

                            const commonProps = {
                                from,
                                to,
                                usageHours,
                                timeZone,
                                hideCharts,
                            };

                            return (
                                <>
                                    {availableSensors.map((sensor, index) => (
                                        <React.Fragment key={`sensor-${sensor}`}>
                                            {index > 0 && <div className={styles.divider} />}
                                            <SensorReportBase
                                                sensor={sensor}
                                                sensorData={safeSensorReports[sensor]}
                                                sensorThreshold={safeThresholds[sensor]}
                                                {...commonProps}
                                            />
                                        </React.Fragment>
                                    ))}
                                </>
                            );
                        })()}
                        <div className={styles.divider} />
                    </div>
                )}
            </ReactPlaceholder>
        </div>
    );
}

export default ReportView;
